import { DetailSections } from '../components/ui/DetailSections';
import { detailSections } from '../config/detailSections';
import { useDetailSection } from '../hooks/useDetailSection';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  UserCheck,
  ClipboardList,
  Award,
  ShieldCheck,
  Activity,
  Pencil,
  Trash2,
  PartyPopper,
  Briefcase,
} from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { cn } from '../utils/cn';
import { eventTeamApiService } from '../services/eventTeamApi';
import type {
  EventAssignment,
  EventAssignmentStatus,
  EventMemberPointsResponse,
  EventMemberPrefixesResponse,
  EventTask,
  EventTaskStatus,
  EventTeamMember,
} from '../types/eventTeam';
import { getEventTeamPerms } from '../utils/eventTeamPermissions';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreatorProfile } from '../components/CreatorProfile';
import { EventTaskStatusBadge } from '../components/eventteam/EventTaskStatusBadge';
import { PrefixBadge } from '../components/eventteam/PrefixBadge';
import { EventActivityLog } from '../components/eventteam/EventActivityLog';
import { EventMemberModal } from '../components/eventteam/EventMemberModal';
import { AwardPointsModal } from '../components/eventteam/AwardPointsModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

type TabId = 'assignments' | 'tasks' | 'points' | 'prefixes' | 'activity';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'assignments', label: 'Assignments', icon: UserCheck },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList },
  { id: 'points', label: 'Player rewards', icon: Award },
  { id: 'prefixes', label: 'Prefixes', icon: ShieldCheck },
  { id: 'activity', label: 'Activity', icon: Activity },
];

const ENTRY_TYPE_CHIP: Record<string, string> = {
  award: 'bg-green-500/15 text-green-300 border-green-500/25',
  deduction: 'bg-red-500/15 text-red-300 border-red-500/25',
  adjustment: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
  prefix_redemption: 'bg-violet-500/15 text-violet-300 border-violet-500/25',
};

function parseSpecialties(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
}

/** Member drill-down (guide §1.7): assignments · tasks · points · prefixes · activity. */
export const EventMemberDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getEventTeamPerms(identity, isDev), [identity, isDev]);

  const [member, setMember] = useState<EventTeamMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useDetailSection(TABS, 'assignments');

  const [assignments, setAssignments] = useState<EventAssignment[] | null>(null);
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState<EventAssignmentStatus | ''>('');
  const [tasks, setTasks] = useState<EventTask[] | null>(null);
  const [taskStatusFilter, setTaskStatusFilter] = useState<EventTaskStatus | ''>('');
  const [points, setPoints] = useState<EventMemberPointsResponse | null>(null);
  const [prefixes, setPrefixes] = useState<EventMemberPrefixesResponse | null>(null);
  const [tabError, setTabError] = useState<string | null>(null);
  const [tabLoading, setTabLoading] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAward, setShowAward] = useState(false);

  const loadMember = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const data = await eventTeamApiService.getTeamMember(id);
      setMember(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team member');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void loadMember();
  }, [loadMember]);

  useDocumentTitle(member ? `Member: ${member.display_name}` : 'Event Team Member');

  const loadTab = useCallback(async () => {
    if (!id) return;
    setTabLoading(true);
    setTabError(null);
    try {
      if (activeTab === 'assignments') {
        setAssignments(await eventTeamApiService.getTeamMemberAssignments(id, assignmentStatusFilter));
      } else if (activeTab === 'tasks') {
        setTasks(await eventTeamApiService.getTeamMemberTasks(id, taskStatusFilter));
      } else if (activeTab === 'points') {
        setPoints(await eventTeamApiService.getTeamMemberPoints(id));
      } else if (activeTab === 'prefixes') {
        setPrefixes(await eventTeamApiService.getTeamMemberPrefixes(id));
      }
    } catch (err) {
      setTabError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setTabLoading(false);
    }
  }, [id, activeTab, assignmentStatusFilter, taskStatusFilter]);

  useEffect(() => {
    void loadTab();
  }, [loadTab]);

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await eventTeamApiService.deleteTeamMember(id);
      navigate('/event-team?tab=team');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete team member');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="space-y-2">
          <SkeletonLoader className="h-3 w-24" />
          <SkeletonLoader className="h-7 w-64 max-w-full" />
          <div className="flex gap-2 pt-1">
            <SkeletonLoader className="h-5 w-16 rounded-full" />
            <SkeletonLoader className="h-5 w-16 rounded-full" />
          </div>
        </div>
        <div className="glass-card">
          <div className="flex gap-4 px-4 py-3 border-b border-glass-200/10">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-4 w-16" />
            ))}
          </div>
          <div className="p-4 md:p-5 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !member || !id) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Event Team"
          title="Team member"
          onBack={() => navigate('/event-team?tab=team')}
          backLabel="Event Team"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load team member"
          description={error || 'Member not found'}
          action={
            <>
              <button type="button" onClick={() => void loadMember()} className="glass-button">
                Retry
              </button>
              <button
                type="button"
                onClick={() => navigate('/event-team?tab=team')}
                className="glass-button-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Event Team
              </button>
            </>
          }
        />
      </div>
    );
  }

  const specialties = parseSpecialties(member.specialties);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Event Team"
        backTo="/event-team?tab=team"
        backLabel="Event Team"
        title={member.display_name}
        titleAdornment={
          <>
            <span className="badge bg-violet-500/15 text-violet-300 border-violet-500/25 capitalize">{member.role}</span>
            <span
              className={cn(
                'badge badge-dot capitalize',
                member.status === 'active'
                  ? 'badge-success'
                  : member.status === 'on_leave'
                    ? 'badge-warning'
                    : 'badge-neutral',
              )}
            >
              {member.status.replace(/_/g, ' ')}
            </span>
            {specialties.map((s) => (
              <span key={s} className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">
                {s}
              </span>
            ))}
          </>
        }
        description={member.notes ? <span className="italic">{member.notes}</span> : undefined}
        actions={
          <>
            {perms.canPoints && (
              <button type="button" onClick={() => setShowAward(true)} className="glass-button text-yellow-300">
                <Award className="w-4 h-4" />
                Award points
              </button>
            )}
            {perms.canAdmin && (
              <>
                <button type="button" onClick={() => setIsEditOpen(true)} className="glass-button-secondary">
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
                <button type="button" onClick={() => setIsDeleteOpen(true)} className="btn-danger">
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </>
            )}
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <PlayerProfile uuid={member.minecraft_uuid} name={member.display_name} />
          <p className="text-xs text-glass-500 inline-flex items-center gap-1 flex-wrap tabular-nums">
            Joined {formatDistanceToNow(new Date(member.joined_at), { addSuffix: true })} · added by{' '}
            <CreatorProfile creatorId={member.added_by} inline />
          </p>
        </div>
      </PageHeader>

      {/* Tabs */}
      <DetailSections sections={detailSections('eventMember', TABS, {  })} active={activeTab} onChange={setActiveTab}>


          {tabError && (
            <div className="alert alert-danger mb-3" role="alert">
              <AlertCircle />
              <span className="flex-1">{tabError}</span>
            </div>
          )}

          {tabLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 animate-spin text-accent-400" />
            </div>
          ) : (
            <>
              {activeTab === 'assignments' && (
                <div className="space-y-3">
                  <div className="segmented" role="tablist" aria-label="Filter assignments by status">
                    {(['', 'active', 'completed', 'removed'] as const).map((s) => (
                      <button
                        key={s || 'all'}
                        type="button"
                        onClick={() => setAssignmentStatusFilter(s)}
                        className="segmented-item segmented-item-sm capitalize"
                        data-active={assignmentStatusFilter === s ? 'true' : 'false'}
                      >
                        {s || 'All'}
                      </button>
                    ))}
                  </div>
                  {(assignments ?? []).length === 0 ? (
                    <EmptyState bare compact icon={UserCheck} title="No assignments" />
                  ) : (
                    <div className="divide-y divide-glass-200/10">
                      {(assignments ?? []).map((a) => (
                        <button
                          key={a.id}
                          onClick={() =>
                            navigate(
                              a.target_type === 'event'
                                ? `/event-team/events/${a.target_id}`
                                : `/event-team/projects/${a.target_id}`,
                            )
                          }
                          className="w-full flex items-center justify-between gap-2 px-2 py-2.5 rounded-md hover:bg-glass-200/5 transition-colors text-left flex-wrap"
                        >
                          <span className="text-sm text-white truncate inline-flex items-center gap-1.5 min-w-0">
                            {a.target_type === 'event' ? (
                              <PartyPopper className="w-3.5 h-3.5 text-accent-400 shrink-0" />
                            ) : (
                              <Briefcase className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                            )}
                            <span className="truncate font-mono text-xs">{a.target_id}</span>
                          </span>
                          <span className="flex items-center gap-2 flex-shrink-0 text-xs">
                            <span className="badge badge-accent capitalize">{a.role}</span>
                            {a.self_assigned === 1 && (
                              <span className="badge badge-purple">self</span>
                            )}
                            <span className={cn('badge badge-dot capitalize', a.status === 'active' ? 'badge-success' : 'badge-neutral')}>
                              {a.status}
                            </span>
                            <span className="text-glass-500 tabular-nums">
                              {formatDistanceToNow(new Date(a.assigned_at), { addSuffix: true })}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'tasks' && (
                <div className="space-y-3">
                  <div className="segmented" role="tablist" aria-label="Filter tasks by status">
                    {(['', 'todo', 'in_progress', 'done', 'skipped'] as const).map((s) => (
                      <button
                        key={s || 'all'}
                        type="button"
                        onClick={() => setTaskStatusFilter(s)}
                        className="segmented-item segmented-item-sm capitalize"
                        data-active={taskStatusFilter === s ? 'true' : 'false'}
                      >
                        {(s || 'All').replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                  {(tasks ?? []).length === 0 ? (
                    <EmptyState bare compact icon={ClipboardList} title="No tasks assigned" />
                  ) : (
                    <div className="divide-y divide-glass-200/10">
                      {(tasks ?? []).map((t) => {
                        const overdue =
                          t.due_date != null && t.status !== 'done' && t.status !== 'skipped' && isPast(new Date(t.due_date));
                        return (
                          <button
                            key={t.id}
                            onClick={() => navigate(`/event-team/events/${t.event_id}`)}
                            className="w-full flex items-center justify-between gap-2 px-2 py-2.5 rounded-md hover:bg-glass-200/5 transition-colors text-left flex-wrap"
                          >
                            <span className="text-sm text-white truncate">{t.title}</span>
                            <span className="flex items-center gap-2 flex-shrink-0 text-xs">
                              <EventTaskStatusBadge status={t.status} />
                              {t.due_date && (
                                <span className={overdue ? 'text-red-400' : 'text-glass-500'}>
                                  Due {format(new Date(t.due_date), 'MMM d')}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'points' && (
                <div className="space-y-3">
                  <p className="text-sm text-glass-400">Rewards this player earned by participating in events. Points are available to all players and do not measure staff contributions.</p>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="subcard px-4 py-3 inline-flex items-center gap-3">
                      <span className="icon-tile icon-tile-warning">
                        <Award className="w-4 h-4" />
                      </span>
                      <div>
                        <div className="eyebrow">Player event points</div>
                        <div className="text-2xl font-semibold tracking-tight tabular-nums text-white">{points?.total ?? 0}</div>
                      </div>
                    </div>
                    {perms.canPoints && (
                      <button type="button" onClick={() => setShowAward(true)} className="glass-button-sm">
                        <Award className="w-3.5 h-3.5" />
                        Award / deduct
                      </button>
                    )}
                  </div>
                  {(points?.entries ?? []).length === 0 ? (
                    <EmptyState bare compact icon={Award} title="No ledger entries" />
                  ) : (
                    <div className="divide-y divide-glass-200/10">
                      {(points?.entries ?? []).map((entry) => (
                        <div
                          key={entry.id}
                          className="flex items-center justify-between gap-2 px-2 py-2.5 flex-wrap"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            <span
                              className={cn(
                                'text-sm font-semibold tabular-nums flex-shrink-0',
                                entry.delta >= 0 ? 'text-green-300' : 'text-red-300',
                              )}
                            >
                              {entry.delta >= 0 ? `+${entry.delta}` : entry.delta}
                            </span>
                            <span
                              className={cn(
                                'badge capitalize',
                                ENTRY_TYPE_CHIP[entry.entry_type] ?? ENTRY_TYPE_CHIP.adjustment,
                              )}
                            >
                              {entry.entry_type.replace(/_/g, ' ')}
                            </span>
                            <span className="text-sm text-glass-200 truncate">{entry.reason}</span>
                            {entry.event_id && (
                              <button
                                onClick={() => navigate(`/event-team/events/${entry.event_id}`)}
                                className="text-[11px] text-accent-300 hover:text-accent-200 hover:underline underline-offset-2"
                              >
                                → event
                              </button>
                            )}
                          </div>
                          <span className="text-[11px] text-glass-500 flex-shrink-0 inline-flex items-center gap-1">
                            by <CreatorProfile creatorId={entry.awarded_by} inline /> ·{' '}
                            {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'prefixes' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="eyebrow mb-2">
                      Live holdings ({prefixes?.holdings?.length ?? 0})
                    </h4>
                    {(prefixes?.holdings ?? []).length === 0 ? (
                      <p className="text-glass-400 text-sm">No live prefixes.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(prefixes?.holdings ?? []).map((h) => (
                          <PrefixBadge key={h.prefix_id} prefixJson={h.prefix_json} name={h.name} size="md" />
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="eyebrow mb-2">
                      Event grant history ({prefixes?.grants?.length ?? 0})
                    </h4>
                    {(prefixes?.grants ?? []).length === 0 ? (
                      <p className="text-glass-400 text-sm">No event-team grants recorded.</p>
                    ) : (
                      <div className="divide-y divide-glass-200/10">
                        {(prefixes?.grants ?? []).map((g) => (
                          <div
                            key={g.id}
                            className="flex items-center justify-between gap-2 px-2 py-2.5 flex-wrap"
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm text-glass-200">Prefix #{g.prefix_id}</span>
                              <span
                                className={cn(
                                  'badge badge-dot capitalize',
                                  g.status === 'active' ? 'badge-success' : 'badge-danger',
                                )}
                              >
                                {g.status}
                              </span>
                              {g.points_spent != null && g.points_spent > 0 && (
                                <span className="text-[10px] text-violet-300">−{g.points_spent} pts</span>
                              )}
                              {g.notes && <span className="text-[11px] text-glass-500 italic">{g.notes}</span>}
                            </div>
                            <span className="text-[11px] text-glass-500 flex-shrink-0 inline-flex items-center gap-1">
                              by <CreatorProfile creatorId={g.granted_by} inline /> ·{' '}
                              {formatDistanceToNow(new Date(g.granted_at), { addSuffix: true })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'activity' && <EventActivityLog source={{ kind: 'member', id }} />}
            </>
          )}

      </DetailSections>

      {/* Modals */}
      {isEditOpen && (
        <EventMemberModal
          isOpen={isEditOpen}
          mode="edit"
          member={member}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            void loadMember();
          }}
        />
      )}
      {showAward && (
        <AwardPointsModal
          isOpen={showAward}
          presetMemberUuid={member.minecraft_uuid}
          onClose={() => setShowAward(false)}
          onSuccess={() => void loadTab()}
        />
      )}
      {isDeleteOpen && (
        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() => void handleDelete()}
          title="Remove Team Member"
          message={`Remove ${member.display_name} from the event team roster?`}
          confirmText="Remove"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
